import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Upload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';

// See api/DTOs/BrandingDto.cs.
interface BrandingSettings {
  businessId: string;
  isEnabled: boolean;
  title?: string;
  logoOnLightUrl?: string;
  logoOnDarkUrl?: string;
}

type Variant = 'on-light' | 'on-dark';

interface Props {
  businessId: string;
  businessName: string;
}

// A business's branding (api/Models/BusinessBranding.cs): when enabled, its
// users — and its child businesses' users — see this title and logo in the
// header instead of DMOTRAK; the sidebar keeps Morgan Rural Tech's logo.
// Admins see the default and switch to a client's branding from the account
// menu to check it.
const BusinessBrandingPanel: React.FC<Props> = ({ businessId, businessName }) => {
  const auth = useAuth();
  const { refresh: refreshBranding } = useBranding();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [settings, setSettings] = useState<BrandingSettings | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<Variant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileInputs = { 'on-light': useRef<HTMLInputElement>(null), 'on-dark': useRef<HTMLInputElement>(null) };

  const apply = (data: BrandingSettings) => {
    setSettings(data);
    setIsEnabled(data.isEnabled);
    setTitle(data.title ?? '');
  };

  useEffect(() => {
    (async () => {
      const res = await fetch(`/api/businesses/${businessId}/branding`, { headers: authHeaders() });
      if (res.ok) apply(await res.json());
      else setError('Failed to load branding');
    })();
  }, [businessId, authHeaders]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await fetch(`/api/businesses/${businessId}/branding`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ isEnabled, title }),
      });
      if (!res.ok) throw new Error('Failed to save branding');
      apply(await res.json());
      setSaved(true);
      refreshBranding();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (variant: Variant, file: File) => {
    setUploading(variant);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/businesses/${businessId}/branding/logo/${variant}`, { method: 'PUT', headers: authHeaders(), body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Upload failed');
      }
      apply(await res.json());
      refreshBranding();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setUploading(null);
    }
  };

  const handleRemove = async (variant: Variant) => {
    setError(null);
    const res = await fetch(`/api/businesses/${businessId}/branding/logo/${variant}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) {
      apply(await res.json());
      refreshBranding();
    } else {
      setError('Failed to remove the logo');
    }
  };

  const logoSlot = (variant: Variant, label: string, hint: string, background: string) => {
    const url = variant === 'on-light' ? settings?.logoOnLightUrl : settings?.logoOnDarkUrl;
    return (
      <Box flex="1 1 240px">
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>{hint}</Typography>
        <Paper variant="outlined" sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: background, mb: 1 }}>
          {url ? (
            <img src={url} alt={`${label} preview`} style={{ maxHeight: 96, maxWidth: '90%' }} />
          ) : (
            <Typography variant="caption" color={background === '#ffffff' ? 'text.secondary' : 'grey.400'}>No logo — uses the other one</Typography>
          )}
        </Paper>
        <input
          ref={fileInputs[variant]}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(variant, file);
            e.target.value = '';
          }}
        />
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading === variant ? <CircularProgress size={14} /> : <UploadIcon />}
            disabled={uploading !== null}
            onClick={() => fileInputs[variant].current?.click()}
          >
            Upload
          </Button>
          {url && (
            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleRemove(variant)}>
              Remove
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  if (!settings && !error) {
    return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h6" mb={1}>Branding</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        When enabled, this business&apos;s users — and its child businesses&apos; users — see this title and logo in the header instead of
        DMOTRAK. The sidebar keeps the Morgan Rural Tech logo. Admins see the default; switch to this branding from the account menu to check it.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Branding saved.</Alert>}

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={3}>
        <FormControlLabel control={<Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />} label="Enabled" />
        <TextField
          size="small"
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={businessName}
          helperText={`Replaces DMOTRAK in the header and browser tab — blank uses "${businessName}"`}
          sx={{ minWidth: 320 }}
        />
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={3}>
        {logoSlot('on-light', 'Logo for light themes', 'Shown on the White theme — a dark or coloured logo.', '#ffffff')}
        {logoSlot('on-dark', 'Logo for dark themes', 'Shown on the dark themes — a white or light logo.', '#1e1e1e')}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        PNG, JPEG or WebP up to 1 MB, ideally a transparent PNG about 256 px tall. Convert an SVG to PNG first.
      </Typography>
    </Box>
  );
};

export default BusinessBrandingPanel;
